import React, {useEffect, useState} from 'react';
import {Flex, message, Pagination, Table} from "antd";
import styles from "../../../Monitoring/monitoringTable.module.css";
import MyText from "../../../../components/UI/Text/MyText";
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import {useWindowSize} from "@uidotdev/usehooks";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import WaterService from "../../../../services/WaterService";
import dayjs from "dayjs";

const KordaiMonitoring = () => {
    const size = useWindowSize();
    const { user, isLoading } = useSelector(state => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [waterData, setWaterData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [lastRowData, setLastRowData] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate('/login', { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        fetchData(pagination.current, pagination.pageSize);
        const fetchLastRow = async () => {
            try {
                const data = await WaterService.getLastKordaiWaterData();
                setLastRowData(data);
            } catch (e) {
                setLastRowData(null);
            }
        };
        fetchLastRow();
        // eslint-disable-next-line
    }, []);

    const fetchData = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const data = await WaterService.getKordaiWaterData(page - 1, pageSize);
            setWaterData(data.list);
            setPagination(prev => ({
                ...prev,
                current: page,
                pageSize,
                total: data.pages ? data.pages * pageSize : 0,
            }));
        } catch (error) {
            setWaterData([]);
            setPagination({ current: 1, pageSize: 10, total: 0 });
            message.error("Failed to fetch water data");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: <IntlMessage id="monitoring.waterId" />,
            key: 'waterId',
            width: 130,
            render: (row) => row.waterId
        },
        {
            title: <IntlMessage id="monitoring.time" />,
            key: 'time',
            width: 200,
            render: (row) => row.time ? dayjs(row.time).format('DD.MM.YYYY HH:mm') : ''
        },
        {
            title: <IntlMessage id="monitoring.velocity" />,
            key: 'velocity',
            width: 130,
            render: (row) => row.velocity
        },
        {
            title: <IntlMessage id="monitoring.flow" />,
            key: 'flow',
            width: 130,
            render: (row) => row.flow
        },
        {
            title: <IntlMessage id="monitoring.depth" />,
            key: 'depth',
            width: 130,
            render: (row) => row.depth
        },
        {
            title: <IntlMessage id="monitoring.temp" />,
            key: 'temp',
            width: 130,
            render: (row) => row.temp
        },
    ];

    const handleTableChange = (pagination) => {
        fetchData(pagination.current, pagination.pageSize);
    };

    return (
        <Flex style={{ width: "100%", position: "relative", padding: "20px 20px 20px 20px" }} vertical align={"center"} justify={"center"}>
            <Flex style={{ width: '100%', maxWidth: 1440, position: 'relative', margin: '0 auto' }} gap={20} vertical align={'center'} justify={'flex-start'}>
                <Flex style={{ width: '100%', justifyContent: 'center'}}>
                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger
                        pageSizeOptions={[10, 20, 50, 100]}
                        onChange={(page, pageSize) => fetchData(page, pageSize)}
                    />
                </Flex>
                <Flex style={{ width: '100%', overflowX: 'auto' }}>
                    <Table
                        className={styles.customTable}
                        columns={columns}
                        dataSource={waterData}
                        loading={loading}
                        rowKey="waterId"
                        pagination={false}
                        scroll={{ x: 800, y: 800 }}
                        bordered
                        size="middle"
                        rowClassName={(_, index) => index % 2 === 0 ? styles.evenRow : styles.oddRow}
                    />
                </Flex>
                {lastRowData && (
                    <>
                        <Flex style={{ width: '100%', justifyContent: 'center', marginTop: 32 }}>
                            <MyText style={{ fontSize: '1.7rem', fontWeight: 700, color: '#0050b3' }}>
                                <IntlMessage id="monitoring.lastRowBlockTitle" />
                            </MyText>
                        </Flex>
                        <div className={styles.lastRowBlock}>
                            <Flex vertical gap={24} style={{ width: '100%' }}>
                                <Flex gap={32} style={{ width: '100%' }}>
                                    <Flex className={styles.cell} vertical>
                                        <div className={styles.cellTitle}><IntlMessage id="monitoring.kordaiMeter" /></div>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.velocity" />:</span><span className={styles.measureValue}>{lastRowData.velocity}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.velocity.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.flow" />:</span><span className={styles.measureValue}>{lastRowData.flow}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.flow.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.depth" />:</span><span className={styles.measureValue}>{lastRowData.depth}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.depth.unit" /></span></Flex>
                                        <Flex className={styles.measureRow}><span className={styles.measureLabel}><IntlMessage id="monitoring.temp" />:</span><span className={styles.measureValue}>{lastRowData.temp}</span><span className={styles.measureUnit}><IntlMessage id="monitoring.temp.unit" /></span></Flex>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </div>
                    </>
                )}
            </Flex>
        </Flex>
    );
};

export default KordaiMonitoring;